import { MediaContentTypes } from "constants/contentTypes"
import { HyperLinkBuilder } from "widgets/commonComponents/inline/hyperLink"
import { isHavakVisible, PayWallArctilceIcon } from "widgets/commonComponents/inline/payWallHavak/payWallHavak"
import { LinkedItemFieldDisplay } from "widgets/commonDataObjects/linkedItemsFieldsDisplay"
import { LinkedItemType, VideoSettingsData } from "widgets/commonDataObjects/interfaces"
import { SiteImageData, SiteMediaData, SiteVideoData } from "widgets/interfaces"
import { SlotIconDisplay } from "widgets/commonComponents/inline/slotIconDisplay/slotIconDisplay"
import { HotStoriesMedia } from "./hotStoriesMedia"
import { separateHourFromDateAndFormatToString, calculateShouldAddArticleAutoplayIndicator } from "widgets/widgetsUtils"
import { getDiffFromToday } from "../../../../siteWidgets/formatDateDisplay"
import { secondsToHms } from "../../../../siteWidgets/siteWidgetUtils"
import * as classNames from "classnames"
import Slider from "react-slick"
import { HOT_STORIES_BG } from "../hotStoriesConsts"
import { HotStoriesSiteItem } from "./hotStoriesFront"

interface HotStoriesFrontMobileProps {
    items: HotStoriesSiteItem[]
    width: number
    videoSettings: VideoSettingsData
    fieldsDisplaySettings: LinkedItemFieldDisplay
    gallerySeconds: number
    backgroundColorComponent?: string
    backgroundColorRoof?: string
    backgroundColorLabel?: string
}

interface HotStoriesFrontMobileState {
    activeIndex: number
    autoplayEnabled: boolean
    progressKey: number
    isVideoFullLength: boolean
    isVideoStarted: boolean
    videoRemainingSec: number | null
}

const VIDEO_EVENTS = ["playing", "pause", "volumechange"]
const VIDEO_START_TIMEOUT_MS = 10000
const TAP_MAX_MOVE_PX = 10

export class HotStoriesFrontMobile extends React.Component<HotStoriesFrontMobileProps, HotStoriesFrontMobileState> {
    public static siteScriptName = "HotStoriesFrontMobile"

    private autoTimer: any
    private mediaWidth = 640
    private mediaHeight = 364
    private progressPollTimer: any = null
    private componentRootRef: HTMLDivElement | null = null
    private mediaRootRef: HTMLDivElement | null = null
    private videoProgressFillRef: HTMLDivElement | null = null
    private lastProgressRatio = 0
    private slider: any = null
    private isProgrammaticSlide = false
    private pendingStopAutoplay = false
    private isMuted = true
    private hasVideoPlayed = false
    private touchStartPoint: { x: number, y: number } | null = null
    private videoMediaCache: { source: SiteMediaData, media: SiteVideoData } | null = null

    constructor(props: HotStoriesFrontMobileProps) {
        super(props)
        this.state = {
            activeIndex: 0,
            autoplayEnabled: props.gallerySeconds > 0,
            progressKey: 0,
            isVideoFullLength: false,
            isVideoStarted: false,
            videoRemainingSec: null,
        }
    }

    componentDidMount() {
        this.scheduleAutoAdvance()
        this.startVideoProgressPoll()
        window.YitVideo && window.YitVideo.InfrastructureReInitialize()
    }

    componentDidUpdate(_: HotStoriesFrontMobileProps, prevState: HotStoriesFrontMobileState) {
        if (prevState.activeIndex !== this.state.activeIndex) {
            this.stopVideoProgressPoll()
            this.lastProgressRatio = 0
            window.YitVideo && window.YitVideo.InfrastructureReInitialize()
            this.scheduleAutoAdvance()
            this.startVideoProgressPoll()
        } else if (prevState.autoplayEnabled !== this.state.autoplayEnabled) {
            this.scheduleAutoAdvance()
            this.startVideoProgressPoll()
        }
    }

    componentWillUnmount() {
        this.clearAutoAdvance()
        this.stopVideoProgressPoll()
    }

    private clearAutoAdvance = () => {
        if (this.autoTimer) {
            clearTimeout(this.autoTimer)
            this.autoTimer = null
        }
    }

    private stopAutoplay = () => {
        this.clearAutoAdvance()
        if (this.state.autoplayEnabled) {
            this.setState({ autoplayEnabled: false })
        }
    }

    private isVideoItem = (item: HotStoriesSiteItem) => {
        const media = item && item.media
        if (!media) {
            return false
        }
        return media.mediaType === MediaContentTypes.MEDIA_VIDEO
    }

    private scheduleAutoAdvance = () => {
        this.clearAutoAdvance()
        const { items, gallerySeconds } = this.props
        const { activeIndex, autoplayEnabled, isVideoFullLength } = this.state
        if (!autoplayEnabled || !items || items.length < 2 || !(gallerySeconds > 0)) {
            return
        }
        const item = items[activeIndex]
        if (this.isVideoItem(item) && isVideoFullLength) {
            return
        }
        if (this.isWaitingForVideoStart(item)) {
            this.autoTimer = setTimeout(this.onVideoStartTimeout, VIDEO_START_TIMEOUT_MS)
            return
        }
        this.autoTimer = setTimeout(() => {
            this.goToIndex((activeIndex + 1) % items.length, false)
        }, gallerySeconds * 1000)
    }

    private isWaitingForVideoStart = (item: HotStoriesSiteItem) => {
        return this.isVideoItem(item) && (item.media as SiteVideoData).isAutoPlaying !== false && !this.state.isVideoStarted
    }

    private onVideoStartTimeout = () => {
        this.setState(state => ({ isVideoStarted: true, progressKey: state.progressKey + 1 }), this.scheduleAutoAdvance)
    }

    private isFullLengthVideo = (video: HTMLVideoElement) => {
        return !video.paused && !video.ended && !video.muted
    }

    private onVideoEvent = (e: Event) => {
        const video = e.target as HTMLVideoElement
        if (!video || video.tagName !== "VIDEO" || video.closest(".slick-cloned")) {
            return
        }
        if (e.type === "volumechange") {
            this.isMuted = video.muted
        }
        const { items } = this.props
        const item = items && items[this.state.activeIndex]
        if (!item || !this.isVideoItem(item) || video.ended) {
            return
        }
        const isFirstPlay = e.type === "playing" && !this.hasVideoPlayed
        if (isFirstPlay) {
            this.hasVideoPlayed = true
        }
        const isVideoFullLength = this.isFullLengthVideo(video)
        const isVideoStarted = this.state.isVideoStarted || isFirstPlay
        if (isFirstPlay || isVideoFullLength !== this.state.isVideoFullLength) {
            this.setState(state => ({ isVideoFullLength, isVideoStarted, progressKey: state.progressKey + 1 }), this.scheduleAutoAdvance)
        }
    }

    private onVideoTimeUpdate = (e: Event) => {
        const video = e.target as HTMLVideoElement
        if (!video || video.tagName !== "VIDEO" || video.closest(".slick-cloned") || !isFinite(video.duration)) {
            return
        }
        const item = this.props.items && this.props.items[this.state.activeIndex]
        if (!item || !this.isVideoItem(item)) {
            return
        }
        const videoRemainingSec = Math.max(0, Math.floor(video.duration - video.currentTime))
        if (videoRemainingSec !== this.state.videoRemainingSec) {
            this.setState({ videoRemainingSec })
        }
    }

    private advanceFromVideo = () => {
        const { items } = this.props
        const { activeIndex, autoplayEnabled } = this.state
        if (!autoplayEnabled || !items || items.length < 2) {
            return
        }
        if (!this.isVideoItem(items[activeIndex])) {
            return
        }
        this.goToIndex((activeIndex + 1) % items.length, false)
    }

    private goToIndex = (index: number, stopAuto: boolean) => {
        const { items } = this.props
        if (!items || items.length === 0) {
            return
        }
        if (index === this.state.activeIndex) {
            if (stopAuto) {
                this.stopAutoplay()
            }
            return
        }
        if (stopAuto) {
            this.pendingStopAutoplay = true
        }
        if (this.slider && items.length > 1) {
            this.isProgrammaticSlide = true
            this.slider.slickGoTo(this.toSlickIndex(index))
            return
        }
        this.applyActiveIndex(index, stopAuto)
    }

    private onSelectItem = (index: number) => {
        this.goToIndex(index, false)
    }

    private onSliderSwipe = () => {
        this.pendingStopAutoplay = true
    }

    private toSlickIndex = (logicalIndex: number) => {
        const { items } = this.props
        return items.length - 1 - logicalIndex
    }

    private toLogicalIndex = (slickIndex: number) => {
        const { items } = this.props
        return items.length - 1 - slickIndex
    }

    private applyActiveIndex = (index: number, stopAutoplay: boolean) => {
        const { items } = this.props
        if (!items || index < 0 || index >= items.length) {
            return
        }
        if (index === this.state.activeIndex) {
            if (stopAutoplay) {
                this.stopAutoplay()
            }
            return
        }
        this.stopVideoProgressPoll()
        this.lastProgressRatio = 0
        this.videoMediaCache = null
        this.hasVideoPlayed = false
        this.setState(state => ({
            activeIndex: index,
            progressKey: state.progressKey + 1,
            autoplayEnabled: stopAutoplay ? false : state.autoplayEnabled,
            isVideoFullLength: false,
            isVideoStarted: false,
            videoRemainingSec: null,
        }))
    }

    private onBeforeChange = () => {
        this.clearAutoAdvance()
        this.stopVideoProgressPoll()
    }

    private onAfterChange = (index: number) => {
        this.isProgrammaticSlide = false
        const logical = this.toLogicalIndex(index)
        this.applyActiveIndex(logical, this.pendingStopAutoplay)
        this.pendingStopAutoplay = false
        window.YitVideo && window.YitVideo.InfrastructureReInitialize()
    }

    private onTouchStart = (e: TouchEvent) => {
        const touch = e.touches[0]
        this.touchStartPoint = touch ? { x: touch.clientX, y: touch.clientY } : null
    }

    private onTouchEnd = (e: TouchEvent) => {
        const start = this.touchStartPoint
        const touch = e.changedTouches[0]
        this.touchStartPoint = null
        if (!start || !touch || Math.abs(touch.clientX - start.x) > TAP_MAX_MOVE_PX || Math.abs(touch.clientY - start.y) > TAP_MAX_MOVE_PX) {
            return
        }
        const target = e.target as HTMLElement
        if (!target || !target.closest || target.closest(".fp-volume-control, .fp-header")) {
            return
        }
        const player = target.closest(".flowplayer") as HTMLElement
        // Flowplayer's first tap only reveals the controls; once it is "is-touched" it toggles playback itself
        if (!player || player.closest(".slick-cloned") || player.classList.contains("is-touched")) {
            return
        }
        const video = player.querySelector("video")
        if (video && !video.paused) {
            video.pause()
        }
    }

    private setComponentRoot = (el: HTMLDivElement | null) => {
        if (this.componentRootRef) {
            VIDEO_EVENTS.forEach(type => this.componentRootRef.removeEventListener(type, this.onVideoEvent, true))
            this.componentRootRef.removeEventListener("timeupdate", this.onVideoTimeUpdate, true)
            this.componentRootRef.removeEventListener("touchstart", this.onTouchStart, true)
            this.componentRootRef.removeEventListener("touchend", this.onTouchEnd, true)
        }
        this.componentRootRef = el
        if (el) {
            VIDEO_EVENTS.forEach(type => el.addEventListener(type, this.onVideoEvent, true))
            el.addEventListener("timeupdate", this.onVideoTimeUpdate, true)
            el.addEventListener("touchstart", this.onTouchStart, true)
            el.addEventListener("touchend", this.onTouchEnd, true)
        }
    }

    private setMediaRoot = (el: HTMLDivElement | null) => {
        this.mediaRootRef = el
    }

    private setVideoProgressFill = (el: HTMLDivElement | null) => {
        this.videoProgressFillRef = el
        if (el) {
            el.style.width = `${Math.round(this.lastProgressRatio * 1000) / 10}%`
        }
    }

    private stopVideoProgressPoll = () => {
        if (this.progressPollTimer) {
            clearInterval(this.progressPollTimer)
            this.progressPollTimer = null
        }
    }

    private findActiveVideoEl = (): HTMLVideoElement | null => {
        const root = this.componentRootRef
        if (!root) {
            return null
        }
        const searchRoots: HTMLElement[] = []
        const activeSlide = root.querySelector(".slick-slide.slick-active") as HTMLElement
        if (activeSlide) {
            searchRoots.push(activeSlide)
        }
        if (this.mediaRootRef && searchRoots.indexOf(this.mediaRootRef) < 0) {
            searchRoots.push(this.mediaRootRef)
        }
        searchRoots.push(root)

        for (let r = 0; r < searchRoots.length; r++) {
            const videos = searchRoots[r].querySelectorAll("video")
            for (let i = 0; i < videos.length; i++) {
                const v = videos[i] as HTMLVideoElement
                if (!v.paused && v.duration > 0 && isFinite(v.duration)) {
                    return v
                }
            }
        }
        for (let r = 0; r < searchRoots.length; r++) {
            const videos = searchRoots[r].querySelectorAll("video")
            for (let i = 0; i < videos.length; i++) {
                const v = videos[i] as HTMLVideoElement
                if (v.duration > 0 && isFinite(v.duration)) {
                    return v
                }
            }
        }
        return null
    }

    private syncVideoProgressFromDom = () => {
        const fill = this.videoProgressFillRef
        const video = this.findActiveVideoEl()
        if (!fill || !video || !(video.duration > 0) || !isFinite(video.duration)) {
            return
        }
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || !this.isVideoItem(items[activeIndex])) {
            return
        }
        const time = video.currentTime
        if (!(time >= 0) || !isFinite(time)) {
            return
        }
        const next = Math.min(1, Math.max(0, time / video.duration))
        if (Math.abs(next - this.lastProgressRatio) < 0.005) {
            return
        }
        this.lastProgressRatio = next
        fill.style.width = `${Math.round(next * 1000) / 10}%`
    }

    private startVideoProgressPoll = () => {
        this.stopVideoProgressPoll()
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || !this.isVideoItem(items[activeIndex])) {
            return
        }
        this.progressPollTimer = setInterval(this.syncVideoProgressFromDom, 100)
    }

    private onPlayStarted = () => {
        if (!this.progressPollTimer) {
            this.startVideoProgressPoll()
        }
    }

    private onVideoEnded = () => {
        this.lastProgressRatio = 1
        if (this.videoProgressFillRef) {
            this.videoProgressFillRef.style.width = "100%"
        }
        this.advanceFromVideo()
    }

    private onVideoError = () => {
        this.advanceFromVideo()
    }

    private formatMetaParts = (item: HotStoriesSiteItem) => {
        const { fieldsDisplaySettings } = this.props
        const author = fieldsDisplaySettings.isAuthorVisible && item.author ? item.author : ""
        let date = ""
        if (fieldsDisplaySettings.isDateVisible && item.itemType === LinkedItemType.ARTICLE && item.dateUpdatedOnSite) {
            const isTodayDate = getDiffFromToday(new Date(item.dateUpdatedOnSite)) === 0
            date = separateHourFromDateAndFormatToString(item.dateUpdatedOnSite, {
                showDate: !isTodayDate,
                showHour: isTodayDate,
            })
        }
        return { author, date }
    }

    private getActiveMedia = (item: HotStoriesSiteItem): SiteMediaData => {
        const media = item.media
        if (!media) {
            return { url: "", mediaType: MediaContentTypes.MEDIA_IMAGE, title: "", className: "", alt: "", ariaHidden: true } as SiteImageData
        }
        if (media.mediaType === MediaContentTypes.MEDIA_VIDEO) {
            if (!this.videoMediaCache || this.videoMediaCache.source !== media) {
                this.videoMediaCache = {
                    source: media,
                    media: { ...media, isMuteAvailable: this.isMuted, isSoundButtonVisible: true } as SiteVideoData,
                }
            }
            return this.videoMediaCache.media
        }
        return media
    }

    private getSlideMedia = (item: HotStoriesSiteItem, isActive: boolean): SiteMediaData => {
        const media = isActive || !item.media ? this.getActiveMedia(item) : item.media
        if (!isActive && media.mediaType === MediaContentTypes.MEDIA_VIDEO) {
            const video = media as SiteVideoData
            return {
                url: video.poster || "",
                mediaType: MediaContentTypes.MEDIA_IMAGE,
                title: video.title || "",
                className: "",
                alt: "",
                ariaHidden: true,
                credit: video.credit,
            } as SiteImageData
        }
        return media
    }

    private renderStory = (item: HotStoriesSiteItem, index: number) => {
        const { fieldsDisplaySettings, videoSettings, backgroundColorRoof, backgroundColorLabel } = this.props
        const { activeIndex, videoRemainingSec } = this.state
        const isActive = index === activeIndex
        const hasLink = !!(item.titleLink)
        const media = this.getSlideMedia(item, isActive)
        const isVideo = media.mediaType === MediaContentTypes.MEDIA_VIDEO
        const videoMedia = isVideo ? media as SiteVideoData : null
        const forcePlayVisible = !!(videoMedia && videoMedia.isAutoPlaying === false)
        const videoDuration = this.isVideoItem(item) ? (item.media as SiteVideoData).duration : 0
        const videoTimeLeft = isActive && videoRemainingSec !== null ? videoRemainingSec : videoDuration
        const credit = (media as SiteImageData).credit
        const isPlus = !this.isVideoItem(item) && !!isHavakVisible(item)
        const roofStyle = backgroundColorRoof ? { backgroundColor: backgroundColorRoof } : undefined
        const labelStyle = backgroundColorLabel ? { backgroundColor: backgroundColorLabel } : undefined
        const { author, date } = this.formatMetaParts(item)

        const titleNode = fieldsDisplaySettings.isTitleVisible && item.title && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <h2 className="hotStoriesTitle" dangerouslySetInnerHTML={{ __html: item.title }} />
                </HyperLinkBuilder>
                : <h2 className="hotStoriesTitle" dangerouslySetInnerHTML={{ __html: item.title }} />
        )

        const subTitleNode = fieldsDisplaySettings.isSubTitleVisible && item.subTitle && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesSubTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <p className="hotStoriesSubTitle" dangerouslySetInnerHTML={{ __html: item.subTitle }} />
                </HyperLinkBuilder>
                : <p className="hotStoriesSubTitle" dangerouslySetInnerHTML={{ __html: item.subTitle }} />
        )

        const cta = hasLink && item.buttonTitle
            ? <HyperLinkBuilder className="hotStoriesCta" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                <span className="hotStoriesCtaText">{item.buttonTitle.replace(/\s*>\s*$/, "").trim() || item.buttonTitle}</span>
            </HyperLinkBuilder>
            : null

        const authorNode = author && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesMetaAuthorLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <span className="hotStoriesMetaAuthor">{author}</span>
                </HyperLinkBuilder>
                : <span className="hotStoriesMetaAuthor">{author}</span>
        )

        const metaNode = (author || date) && (
            <div className="hotStoriesMeta">
                {authorNode}
                {author && date && <span className="hotStoriesMetaSep"> | </span>}
                {date && <span className="hotStoriesMetaDate">{date}</span>}
            </div>
        )

        return (
            <div className="hotStoriesStack" key={item.itemId || index} dir="rtl">
                <div
                    className={classNames("hotStoriesMedia", { forcePlayVisible })}
                    ref={isActive ? this.setMediaRoot : undefined}
                >
                    <HotStoriesMedia
                        data={media}
                        width={this.mediaWidth}
                        height={this.mediaHeight}
                        hideVideoAds={item.hideVideoAds !== false || !!(videoSettings && videoSettings.hideVideoAds)}
                        isImageLinkable={hasLink && !isVideo}
                        itemLinkUrl={item.titleLink}
                        linkTarget={item.titleTargetLink}
                        articleId={item.itemId}
                        onPlayStarted={isActive ? this.onPlayStarted : undefined}
                        onVideoEnded={isActive ? this.onVideoEnded : undefined}
                        onVideoError={isActive ? this.onVideoError : undefined}
                    />
                    {!isPlus && item.redLabelText &&
                        <span className="hotStoriesMediaLabel" style={labelStyle}>
                            {item.redLabelText}
                        </span>}
                    {videoDuration > 0 &&
                        <div className="hotStoriesVideoTime">
                            <span className="hotStoriesVideoTimeIcon" />
                            <span>{secondsToHms(videoTimeLeft)}</span>
                        </div>}
                    {isPlus &&
                        <PayWallArctilceIcon className="hotStoriesPlusIcon" premiumExpireDays={item.premiumExpireDays} dateUpdatedOnSite={item.dateUpdatedOnSite} />}
                    <SlotIconDisplay itemId={item.itemId} icon={item.hasVideo ? "video" : item.icon} iconClassName="big" isIconVisible={calculateShouldAddArticleAutoplayIndicator(item.icon, item.media)} />
                    {credit && <div className="hotStoriesCredit">({credit})</div>}
                </div>
                <div className="hotStoriesCopy">
                    <div className="hotStoriesArticleBody">
                        {fieldsDisplaySettings.isRoofTitleVisible && item.roofTitle &&
                            <span className="hotStoriesRoofChip" style={roofStyle}>{item.roofTitle}</span>}
                        {titleNode}
                        {subTitleNode}
                        {metaNode}
                    </div>
                    {cta}
                </div>
            </div>
        )
    }

    private renderSegment = (item: HotStoriesSiteItem, index: number) => {
        const { activeIndex, autoplayEnabled, progressKey, isVideoFullLength } = this.state
        const { gallerySeconds } = this.props
        const isActive = index === activeIndex
        const isActiveVideo = isActive && this.isVideoItem(item)
        const showVideoProgress = isActiveVideo && (isVideoFullLength || !autoplayEnabled)
        const showFullIndicator = !autoplayEnabled && isActive && !isActiveVideo
        const isWaiting = isActive && this.isWaitingForVideoStart(item)
        const durationSec = autoplayEnabled && isActive && !showVideoProgress && !isWaiting && gallerySeconds > 0 ? gallerySeconds : 0

        return (
            <button
                key={item.itemId || index}
                type="button"
                className={classNames("hotStoriesSegment hotStoriesBarHeight-pf", { active: isActive })}
                onClick={() => this.onSelectItem(index)}
                aria-label={item.title || `story ${index + 1}`}
                aria-current={isActive ? "true" : undefined}
            >
                <div
                    className="hotStoriesProgressTrack hotStoriesBarHeight-pf"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {showVideoProgress &&
                        <div
                            key={progressKey}
                            ref={this.setVideoProgressFill}
                            className="hotStoriesProgressFill synced"
                        />}
                    {showFullIndicator &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill synced"
                            style={{ width: "100%" }}
                        />}
                    {durationSec > 0 &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill"
                            style={{ animationDuration: `${durationSec}s` }}
                        />}
                </div>
            </button>
        )
    }

    public render() {
        const { items, backgroundColorComponent } = this.props
        const { activeIndex } = this.state
        if (!items || items.length === 0) {
            return null
        }

        const canSwipe = items.length > 1
        const sliderSettings = {
            dots: false,
            arrows: false,
            infinite: canSwipe,
            speed: 450,
            slidesToShow: 1,
            slidesToScroll: 1,
            swipe: canSwipe,
            touchMove: canSwipe,
            swipeToSlide: false,
            touchThreshold: 3,
            rtl: false,
            adaptiveHeight: false,
            waitForAnimate: true,
            useCSS: true,
            useTransform: true,
            cssEase: "ease-out",
            initialSlide: this.toSlickIndex(0),
            beforeChange: this.onBeforeChange,
            afterChange: this.onAfterChange,
            onSwipe: this.onSliderSwipe,
        }

        return (
            <div className="HotStoriesFrontMobile" style={{ backgroundColor: backgroundColorComponent || HOT_STORIES_BG }} ref={this.setComponentRoot}>
                <Slider
                    {...sliderSettings}
                    className="hotStoriesSlider"
                    ref={slider => { this.slider = slider }}
                >
                    {items.map((_, slickIndex) => {
                        const index = this.toLogicalIndex(slickIndex)
                        return this.renderStory(items[index], index)
                    })}
                </Slider>
                <div className="hotStoriesStrip">
                    <div className="hotStoriesStripList">
                        {items.map((item, index) => this.renderSegment(item, index))}
                    </div>
                </div>
            </div>
        )
    }
}
